import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Building2, BrainCircuit, BarChart3, Users, Telescope, Layers, BookOpen } from "lucide-react";
import { SKILLS } from "./data";
import { useContent } from "./useContent";

const GROUP_META = {
  Build:    { color: "#e6c769", icon: Building2 },
  Capital:  { color: "#a5b4fc", icon: BarChart3 },
  Analyse:  { color: "#7dd3fc", icon: Telescope },
  Frontier: { color: "#f0abfc", icon: BrainCircuit },
  People:   { color: "#86efac", icon: Users },
};

const Skills = () => {
  const [hover, setHover] = React.useState(null);
  const items = useContent("skills", SKILLS);

  return (
    <section id="skills" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">10 / The Toolkit</div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              A skill <span className="italic">ecosystem</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Not a list of bars. A map of how I think — grouped by intent: things I build, capital I move, ideas I analyse, frontiers I explore, and people I serve.
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((s, i) => {
            const meta = GROUP_META[s.group] || GROUP_META.Build;
            const Icon = meta.icon;
            return (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                onMouseEnter={() => setHover(s.name)}
                onMouseLeave={() => setHover(null)}
                className="group relative rounded-[20px] border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl p-6 transition-all hover:-translate-y-1"
                style={{ borderColor: hover === s.name ? `${meta.color}55` : undefined }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center transition-colors"
                    style={{ background: `${meta.color}14`, color: meta.color }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-[10px] tracking-[0.25em] uppercase font-mono"
                    style={{ color: `${meta.color}` }}
                  >
                    {s.group}
                  </span>
                </div>
                <h4 className="mt-6 text-[22px] text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {s.name}
                </h4>
                <p className="mt-3 text-[13px] text-white/55 leading-relaxed">{s.note}</p>
                <div
                  className="absolute inset-x-0 bottom-0 h-px transition-all opacity-0 group-hover:opacity-100"
                  style={{ background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)` }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
